import { useTranslation } from 'react-i18next';
import {
  Ban,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleHelp,
  CircleSlash,
  Clock,
  DoorOpen,
  FileX,
  OctagonX,
  PowerOff,
  ShieldOff,
  TriangleAlert,
  WifiOff,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Icon } from '@/components/ui/icon';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PrintAttemptOutcome, PrintJobStatus, PrinterStatus } from '@/lib/shared-types';
import type { DeviceHealth, VersionCompliance } from './fleetApi';

/**
 * ## State encoded in form, not only in words
 *
 * This is the 8pm-on-a-Friday requirement. An operator whose tickets have
 * stopped coming out is scanning a list under time pressure, and a status
 * *column* is only readable by stopping and reading it — by which point they
 * have read forty healthy rows to find the one that is not.
 *
 * So every state here carries four independent signals, and a reader only needs
 * one of them to work:
 *
 *  - a **glyph with a different silhouette** per state (a tick, a triangle, an
 *    octagon, a dashed circle) — legible when the colour is not, which covers
 *    both colour blindness and a greasy kitchen screen;
 *  - a **tone**, the app's existing five-colour vocabulary;
 *  - a **rail** — a thick logical-start border down the row, so a broken row has
 *    a spine visible from across the pass without reading anything at all;
 *  - a **row tint** for the two states that mean somebody has to move.
 *
 * `border-s-*` and not `border-l-*`: the rail must sit at the start of the line
 * in Arabic too, and only the logical property flips.
 */
export interface StateVisual {
  icon: LucideIcon;
  tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';
  /** Logical-start border on the row. `transparent` keeps the row width stable. */
  rail: string;
  /** Row background, for the states that need somebody to act. */
  row: string;
  /** Higher sorts first when the screen puts the worst news at the top. */
  severity: number;
}

const QUIET = { rail: 'border-s-transparent', row: '' };

/**
 * The spool, worst first.
 *
 * `DEAD` outranks `FAILED` deliberately: a failed job still has attempts left
 * and may yet print on its own, while a dead one is out of attempts and will
 * never print again without a person. Those are different jobs for the operator
 * and they must not look alike.
 */
export const JOB_VISUALS: Record<PrintJobStatus, StateVisual> = {
  DEAD: {
    icon: OctagonX,
    tone: 'danger',
    rail: 'border-s-red-600 dark:border-s-red-500',
    row: 'bg-red-50/70 dark:bg-red-950/30',
    severity: 5,
  },
  FAILED: {
    icon: TriangleAlert,
    tone: 'danger',
    rail: 'border-s-red-400 dark:border-s-red-500/70',
    row: 'bg-red-50/40 dark:bg-red-950/20',
    severity: 4,
  },
  /** In a bridge's hands. Forward motion, but not paper yet. */
  CLAIMED: {
    icon: CircleDot,
    tone: 'info',
    rail: 'border-s-sky-400 dark:border-s-sky-500',
    row: '',
    severity: 3,
  },
  /** Durable, not delivered. Latency is one poll interval and that is by design. */
  QUEUED: {
    icon: Clock,
    tone: 'info',
    rail: 'border-s-sky-300 dark:border-s-sky-600',
    row: '',
    severity: 2,
  },
  PRINTED: { icon: CircleCheck, tone: 'success', ...QUIET, severity: 1 },
  CANCELLED: { icon: CircleSlash, tone: 'neutral', ...QUIET, severity: 0 },
};

export const DEVICE_HEALTH_VISUALS: Record<DeviceHealth, StateVisual> = {
  OFFLINE: {
    icon: WifiOff,
    tone: 'danger',
    rail: 'border-s-red-500',
    row: 'bg-red-50/40 dark:bg-red-950/20',
    severity: 5,
  },
  DEGRADED: {
    icon: TriangleAlert,
    tone: 'warning',
    rail: 'border-s-amber-400',
    row: '',
    severity: 4,
  },
  /** Deliberately taken out of service. Not a fault — so no alarm tint. */
  REVOKED: { icon: ShieldOff, tone: 'neutral', rail: 'border-s-slate-400', row: '', severity: 3 },
  /** A code is out and nothing has claimed it. A setup task, not an incident. */
  NEVER_SEEN: {
    icon: CircleDashed,
    tone: 'warning',
    rail: 'border-s-amber-300',
    row: '',
    severity: 2,
  },
  HEALTHY: { icon: CircleCheck, tone: 'success', ...QUIET, severity: 1 },
};

/**
 * `UNKNOWN` is not a failure: it is a printer nothing has reported on yet, and
 * collapsing it into `OFFLINE` would put a brand new printer on the alert list.
 */
export const PRINTER_STATUS_VISUALS: Record<PrinterStatus, StateVisual> = {
  ERROR: {
    icon: OctagonX,
    tone: 'danger',
    rail: 'border-s-red-600',
    row: 'bg-red-50/40 dark:bg-red-950/20',
    severity: 6,
  },
  OFFLINE: {
    icon: PowerOff,
    tone: 'danger',
    rail: 'border-s-red-500',
    row: 'bg-red-50/40 dark:bg-red-950/20',
    severity: 5,
  },
  JAMMED: { icon: TriangleAlert, tone: 'danger', rail: 'border-s-red-400', row: '', severity: 4 },
  OUT_OF_PAPER: { icon: FileX, tone: 'warning', rail: 'border-s-amber-400', row: '', severity: 3 },
  COVER_OPEN: { icon: DoorOpen, tone: 'warning', rail: 'border-s-amber-400', row: '', severity: 2 },
  UNKNOWN: { icon: CircleHelp, tone: 'neutral', rail: 'border-s-slate-300', row: '', severity: 1 },
  ONLINE: { icon: CircleCheck, tone: 'success', ...QUIET, severity: 0 },
};

export const VERSION_VISUALS: Record<VersionCompliance, StateVisual> = {
  UNSUPPORTED: { icon: OctagonX, tone: 'danger', rail: 'border-s-red-500', row: '', severity: 3 },
  OUTDATED: { icon: TriangleAlert, tone: 'warning', rail: 'border-s-amber-400', row: '', severity: 2 },
  /** "We do not know" and "it is fine" are different answers. */
  UNKNOWN: { icon: CircleHelp, tone: 'neutral', rail: 'border-s-slate-300', row: '', severity: 1 },
  SUPPORTED: { icon: CircleCheck, tone: 'success', ...QUIET, severity: 0 },
};

/**
 * `LATE` is the one that is not a print failure: it is an ack whose lease had
 * already been reclaimed, and it is the only evidence an operator has that a
 * second copy may exist. It gets a warning rather than a danger tone so it does
 * not read as "this did not print" — it almost certainly did, twice.
 */
export const ATTEMPT_VISUALS: Record<PrintAttemptOutcome, StateVisual> = {
  FAILED: { icon: TriangleAlert, tone: 'danger', ...QUIET, severity: 4 },
  EXPIRED: { icon: Clock, tone: 'warning', ...QUIET, severity: 3 },
  LATE: { icon: CircleDot, tone: 'warning', ...QUIET, severity: 3 },
  CANCELLED: { icon: Ban, tone: 'neutral', ...QUIET, severity: 1 },
  CLAIMED: { icon: CircleDot, tone: 'info', ...QUIET, severity: 2 },
  PRINTED: { icon: CircleCheck, tone: 'success', ...QUIET, severity: 0 },
};

/**
 * A chip that draws its glyph as well as its word.
 *
 * The generic shape keeps every fleet screen using the same pairing: nothing
 * anywhere renders a bare status string, which is what would let a state slip
 * onto the screen with no silhouette of its own.
 */
function StateChip({ visual, label }: { visual: StateVisual; label: string }) {
  return (
    <Badge tone={visual.tone} className="gap-1 whitespace-nowrap">
      <Icon as={visual.icon} size="sm" aria-hidden />
      {label}
    </Badge>
  );
}

export function JobStatusChip({ status }: { status: PrintJobStatus }) {
  const { t } = useTranslation();
  return <StateChip visual={JOB_VISUALS[status]} label={t(`fleet.jobStatus.${status}`)} />;
}

export function DeviceHealthChip({ health }: { health: DeviceHealth }) {
  const { t } = useTranslation();
  return <StateChip visual={DEVICE_HEALTH_VISUALS[health]} label={t(`fleet.health.${health}`)} />;
}

export function PrinterStatusChip({ status }: { status: PrinterStatus }) {
  const { t } = useTranslation();
  return (
    <StateChip visual={PRINTER_STATUS_VISUALS[status]} label={t(`fleet.printerStatus.${status}`)} />
  );
}

export function VersionChip({ compliance }: { compliance: VersionCompliance }) {
  const { t } = useTranslation();
  return (
    <StateChip visual={VERSION_VISUALS[compliance]} label={t(`fleet.compliance.${compliance}`)} />
  );
}

export function AttemptOutcomeChip({ outcome }: { outcome: PrintAttemptOutcome }) {
  const { t } = useTranslation();
  return (
    <StateChip visual={ATTEMPT_VISUALS[outcome]} label={t(`fleet.attemptOutcome.${outcome}`)} />
  );
}

/** The row classes for a state — the rail and, where it earns one, the tint. */
export function rowStateClass(visual: StateVisual | undefined): string {
  if (!visual) return '';
  return cn('border-s-4', visual.rail, visual.row);
}
